import { t } from 'i18next';
import { IUnifiedSubscription, TPurchaseBillingPeriod } from '../../../common/purchases';
import { TXsollaSubscriptionOperation, XSOLLA_TIERS } from '../../../common/xsolla';
import {
  TIER_NAME_TO_KEY,
  TXsollaSubscriptionTier,
  VALID_SUBSCRIPTION_TIERS
} from '../../../common/xsollaConfig';
import { naturalMonthAndDayAndYear } from '../../libs/utils';
import { getActiveXsollaPlanState } from './xsollaActivePlan';

export type TXsollaCurrentPlanSummaryViewModel = {
  tier: TXsollaSubscriptionTier | null;
  tierName: string;
  billingPeriodLabel: string | null;
  summary: string;
  summaryLabel: string;
  dateLabel: string | null;
  isNonRenewing: boolean;
  actionLabel: string | null;
  actionPath: string | null;
};

export type TXsollaTierChangeKind = 'upgrade' | 'downgrade' | 'switch_billing';

export type TXsollaTierCtaViewModel = {
  isCurrentPlan: boolean;
  actionHref: string | null;
  actionLabel: string | null;
  actionNote: string | null;
  currentPlanDetails: string | null;
  isCheckoutBlocked: boolean;
  checkoutOperation: TXsollaSubscriptionOperation | null;
  changeKind: TXsollaTierChangeKind | null;
};

export type TXsollaSubscriptionOptionsViewModel = {
  activePlanSummary: TXsollaCurrentPlanSummaryViewModel | null;
  tierCtas: Record<TXsollaSubscriptionTier, TXsollaTierCtaViewModel>;
};

export function getBillingPeriodLabel(billingPeriod: IUnifiedSubscription['billingPeriod']): string | null {
  if (billingPeriod === 'annual') {
    return t('common:terms.yearly');
  }

  if (billingPeriod === 'monthly') {
    return t('common:terms.monthly');
  }

  return null;
}

function getTierName(tier: TXsollaSubscriptionTier | null): string {
  return tier
    ? t(`patreon:ranks.${tier}`)
    : t('settings:subscriptions.direct-payment');
}

function getTierOrdinal(tier: TXsollaSubscriptionTier): number {
  return XSOLLA_TIERS[TIER_NAME_TO_KEY[tier]].ordinal;
}

function buildChangePlanLabel(params: {
  currentTier: TXsollaSubscriptionTier;
  targetTier: TXsollaSubscriptionTier;
  selectedBillingPeriod: Extract<TPurchaseBillingPeriod, 'monthly' | 'annual'>;
}): string {
  const { currentTier, selectedBillingPeriod, targetTier } = params;

  if (currentTier === targetTier) {
    return t(
      selectedBillingPeriod === 'annual'
        ? 'xsolla:switch-to-yearly'
        : 'xsolla:switch-to-monthly'
    );
  }

  return t(
    getTierOrdinal(targetTier) > getTierOrdinal(currentTier)
      ? 'xsolla:upgrade-to-tier'
      : 'xsolla:downgrade-to-tier',
    { tierName: getTierName(targetTier) }
  );
}

function getChangeKind(
  currentTier: TXsollaSubscriptionTier,
  targetTier: TXsollaSubscriptionTier
): TXsollaTierChangeKind {
  if (currentTier === targetTier) {
    return 'switch_billing';
  }

  return getTierOrdinal(targetTier) > getTierOrdinal(currentTier) ? 'upgrade' : 'downgrade';
}

export function buildXsollaSubscriptionOptionsViewModel(params: {
  subscriptions: IUnifiedSubscription[];
  manageSubscriptionPath?: string | null;
  selectedBillingPeriod: Extract<TPurchaseBillingPeriod, 'monthly' | 'annual'>;
}): TXsollaSubscriptionOptionsViewModel {
  const {
    manageSubscriptionPath = null,
    subscriptions,
    selectedBillingPeriod
  } = params;
  const activeXsollaPlan = getActiveXsollaPlanState(subscriptions);
  const activeXsollaPlanName = getTierName(activeXsollaPlan?.tier ?? null);
  const activeXsollaBillingLabel = getBillingPeriodLabel(activeXsollaPlan?.billingPeriod ?? null);
  const hasKnownCurrentPlan = !!activeXsollaPlan?.tier && !!activeXsollaPlan.billingPeriod;
  const canOfferPlanChanges = !!activeXsollaPlan &&
    !activeXsollaPlan.isNonRenewing &&
    hasKnownCurrentPlan;
  const activePlanSummary = activeXsollaPlan
    ? {
        tier: activeXsollaPlan.tier,
        tierName: activeXsollaPlanName,
        billingPeriodLabel: activeXsollaBillingLabel,
        summary: [activeXsollaPlanName, activeXsollaBillingLabel].filter(Boolean).join(' · '),
        summaryLabel: t('xsolla:current-summary', {
          summary: [activeXsollaPlanName, activeXsollaBillingLabel].filter(Boolean).join(' · ')
        }),
        dateLabel: activeXsollaPlan.subscriptionEndsAt
          ? `${t(activeXsollaPlan.isNonRenewing
            ? 'settings:subscriptions.current-status.access-through'
            : 'settings:subscriptions.current-status.current-period-ends')}: ${naturalMonthAndDayAndYear(activeXsollaPlan.subscriptionEndsAt)}`
          : null,
        isNonRenewing: activeXsollaPlan.isNonRenewing,
        actionLabel: manageSubscriptionPath ? t('xsolla:success.cta-manage') : null,
        actionPath: manageSubscriptionPath || null
      }
    : null;

  const tierCtas = VALID_SUBSCRIPTION_TIERS.reduce<Record<TXsollaSubscriptionTier, TXsollaTierCtaViewModel>>((result, tier) => {
    const isCurrentTier = activeXsollaPlan?.tier === tier;
    const isExactCurrentPlan = isCurrentTier && activeXsollaPlan?.billingPeriod === selectedBillingPeriod;
    const currentPlanDetails = isCurrentTier && activeXsollaBillingLabel
      ? t('xsolla:current-billing', { billingPeriod: activeXsollaBillingLabel })
      : null;

    if (!activeXsollaPlan) {
      result[tier] = {
        isCurrentPlan: false,
        actionHref: null,
        actionLabel: null,
        actionNote: null,
        currentPlanDetails: null,
        isCheckoutBlocked: false,
        checkoutOperation: null,
        changeKind: null
      };
      return result;
    }

    if (isExactCurrentPlan) {
      result[tier] = {
        isCurrentPlan: true,
        actionHref: null,
        actionLabel: t('xsolla:current-plan-card-action'),
        actionNote: null,
        currentPlanDetails,
        isCheckoutBlocked: true,
        checkoutOperation: null,
        changeKind: null
      };
      return result;
    }

    if (canOfferPlanChanges) {
      result[tier] = {
        isCurrentPlan: isCurrentTier,
        actionHref: null,
        actionLabel: buildChangePlanLabel({
          currentTier: activeXsollaPlan.tier!,
          targetTier: tier,
          selectedBillingPeriod
        }),
        actionNote: null,
        currentPlanDetails,
        isCheckoutBlocked: false,
        checkoutOperation: 'change_plan',
        changeKind: getChangeKind(activeXsollaPlan.tier!, tier)
      };
      return result;
    }

    result[tier] = {
      isCurrentPlan: isCurrentTier,
      actionHref: manageSubscriptionPath || null,
      actionLabel: manageSubscriptionPath
        ? t('xsolla:success.cta-manage')
        : t('xsolla:active-subscription-change-later'),
      actionNote: !isCurrentTier && manageSubscriptionPath
        ? t('xsolla:active-subscription-change-later-note')
        : null,
      currentPlanDetails,
      isCheckoutBlocked: !manageSubscriptionPath,
      checkoutOperation: null,
      changeKind: null
    };

    return result;
  }, {} as Record<TXsollaSubscriptionTier, TXsollaTierCtaViewModel>);

  return {
    activePlanSummary,
    tierCtas
  };
}
